import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAutoScrollDelta } from './auto-scroll';
import { shouldStartDrag } from './drag-gesture';
import { createDragPreviewState, updateDragPreviewState } from './drag-preview';
import {
  getDropIndicatorTarget,
  getInsertionIndexFromTarget,
} from './drop-position';
import {
  createKeyboardDragState,
  dropKeyboardDragItem,
  moveKeyboardDragItem,
  pickKeyboardDragItem,
} from './keyboard-drag';
import { getDestinationIndexFromInsertion } from './reorder-list';
import type {
  DragInputType,
  DragPreviewState,
  DropIndicatorTarget,
  KeyboardDragState,
  SortableItemRect,
} from './types';
import { isFiniteNumber } from './untils';

type PendingPressState = {
  input: DragInputType;
  startX: number;
  startY: number;
  index: number;
  itemKey: string;
};

export type SortableHandlePressPayload = {
  input: DragInputType;
  clientX?: number;
  clientY?: number;
  index: number;
  itemKey: string;
  itemLabel: string;
};

export type UseSortableListDndParams = {
  listLength: number;
  canReorder: boolean;
  getItemRects: () => SortableItemRect[];
  getItemRectById: (itemId: string) => DOMRect | null;
  getListElement: () => HTMLDivElement | null;
  onCommitReorder: (payload: { fromIndex: number; toIndex: number }) => void;
};

export type UseSortableListDndResult = {
  isDragging: boolean;
  dragItemKey: string | null;
  dragSourceIndex: number | null;
  dragPreview: DragPreviewState | null;
  keyboardState: KeyboardDragState;
  activeDropTarget: DropIndicatorTarget | null;
  liveMessage: string;
  isKeyboardDragging: boolean;
  handlePressStart: (payload: SortableHandlePressPayload) => void;
  consumeSuppressedRowClick: () => boolean;
  isRowActionBlocked: () => boolean;
};

/**
 * Centralizes sortable drag-and-drop interaction lifecycle for pointer, touch and keyboard.
 */
export function useSortableListDnd({
  listLength,
  canReorder,
  getItemRects,
  getItemRectById,
  getListElement,
  onCommitReorder,
}: UseSortableListDndParams): UseSortableListDndResult {
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragSourceIndex, setDragSourceIndex] = useState<number | null>(null);
  const [dragItemKey, setDragItemKey] = useState<string | null>(null);
  const [dragPreview, setDragPreview] = useState<DragPreviewState | null>(null);
  const [dropTarget, setDropTarget] = useState<DropIndicatorTarget | null>(
    null
  );
  const [keyboardState, setKeyboardState] = useState<KeyboardDragState>(
    createKeyboardDragState()
  );
  const [liveMessage, setLiveMessage] = useState<string>('');

  const pendingPressRef = useRef<PendingPressState | null>(null);
  const suppressNextRowClickRef = useRef<boolean>(false);

  const resetPointerDrag = useCallback((): void => {
    pendingPressRef.current = null;
    setIsDragging(false);
    setDragSourceIndex(null);
    setDragItemKey(null);
    setDragPreview(null);
    setDropTarget(null);
  }, []);

  const commitReorder = useCallback(
    ({ fromIndex, toIndex }: { fromIndex: number; toIndex: number }): void => {
      if (!canReorder) return;
      if (fromIndex === toIndex) return;
      if (fromIndex < 0 || toIndex < 0) return;
      if (fromIndex >= listLength || toIndex >= listLength) return;

      onCommitReorder({ fromIndex, toIndex });
    },
    [canReorder, listLength, onCommitReorder]
  );

  const keyboardDropTarget = useMemo<DropIndicatorTarget | null>(() => {
    if (keyboardState.mode !== 'dragging') return null;
    if (
      keyboardState.currentIndex === null ||
      keyboardState.originIndex === null
    ) {
      return null;
    }

    if (keyboardState.currentIndex > keyboardState.originIndex) {
      return {
        index: keyboardState.currentIndex,
        side: 'after',
      };
    }

    return {
      index: keyboardState.currentIndex,
      side: 'before',
    };
  }, [keyboardState]);

  const startPointerDrag = useCallback(
    ({
      pointerX,
      pointerY,
      index,
      itemKey,
    }: {
      pointerX: number;
      pointerY: number;
      index: number;
      itemKey: string;
    }): void => {
      const itemRect = getItemRectById(itemKey);
      if (!itemRect) return;

      setIsDragging(true);
      setDragSourceIndex(index);
      setDragItemKey(itemKey);
      suppressNextRowClickRef.current = true;
      setDragPreview(
        createDragPreviewState({
          pointerX,
          pointerY,
          itemLeft: itemRect.left,
          itemTop: itemRect.top,
        })
      );
      setDropTarget(
        getDropIndicatorTarget({
          pointerY,
          itemRects: getItemRects(),
        })
      );
      setLiveMessage('Reorder started');
    },
    [getItemRectById, getItemRects]
  );

  const handlePressStart = useCallback(
    ({
      input,
      clientX,
      clientY,
      index,
      itemKey,
      itemLabel,
    }: SortableHandlePressPayload): void => {
      if (!canReorder) return;

      if (input === 'keyboard') {
        if (keyboardState.mode === 'idle') {
          const nextState = pickKeyboardDragItem({
            state: keyboardState,
            index,
            listLength,
          });

          setKeyboardState(nextState);
          setLiveMessage(`Picked up ${itemLabel}`);
          return;
        }

        const dropResult = dropKeyboardDragItem({
          state: keyboardState,
        });

        setKeyboardState(dropResult.nextState);

        if (
          dropResult.didReorder &&
          dropResult.fromIndex !== null &&
          dropResult.toIndex !== null
        ) {
          commitReorder({
            fromIndex: dropResult.fromIndex,
            toIndex: dropResult.toIndex,
          });
        }

        setLiveMessage('Dropped item');
        return;
      }

      if (!isFiniteNumber(clientX) || !isFiniteNumber(clientY)) {
        return;
      }

      pendingPressRef.current = {
        input,
        startX: clientX,
        startY: clientY,
        index,
        itemKey,
      };
    },
    [canReorder, commitReorder, keyboardState, listLength]
  );

  const consumeSuppressedRowClick = useCallback((): boolean => {
    if (!suppressNextRowClickRef.current) return false;

    suppressNextRowClickRef.current = false;
    return true;
  }, []);

  const isRowActionBlocked = useCallback((): boolean => {
    return isDragging || keyboardState.mode === 'dragging';
  }, [isDragging, keyboardState.mode]);

  useEffect(() => {
    const onMove = ({
      clientX,
      clientY,
    }: {
      clientX: number;
      clientY: number;
    }): void => {
      const pendingPress = pendingPressRef.current;

      if (pendingPress && !isDragging) {
        const shouldBeginDrag =
          pendingPress.input === 'touch'
            ? true
            : shouldStartDrag({
                startX: pendingPress.startX,
                startY: pendingPress.startY,
                currentX: clientX,
                currentY: clientY,
              });

        if (shouldBeginDrag) {
          startPointerDrag({
            pointerX: clientX,
            pointerY: clientY,
            index: pendingPress.index,
            itemKey: pendingPress.itemKey,
          });
        }
      }

      if (!isDragging || !dragPreview) return;

      setDragPreview((previous) => {
        if (!previous) return previous;

        return updateDragPreviewState({
          previous,
          pointerX: clientX,
          pointerY: clientY,
        });
      });

      setDropTarget(
        getDropIndicatorTarget({
          pointerY: clientY,
          itemRects: getItemRects(),
        })
      );

      const listElement = getListElement();
      if (!listElement) return;

      const listRect = listElement.getBoundingClientRect();
      const scrollDelta = getAutoScrollDelta({
        pointerY: clientY,
        containerTop: listRect.top,
        containerHeight: listRect.height,
      });

      if (scrollDelta !== 0) {
        listElement.scrollTop += scrollDelta;
      }
    };

    const onEnd = ({
      clientX,
      clientY,
    }: {
      clientX?: number;
      clientY?: number;
    }): void => {
      const pendingPress = pendingPressRef.current;

      if (pendingPress && !isDragging) {
        pendingPressRef.current = null;
        return;
      }

      if (!isDragging) return;

      const sourceIndex = dragSourceIndex;
      const target = dropTarget;
      const listRect = getListElement()?.getBoundingClientRect();
      const hasFinitePointer =
        isFiniteNumber(clientX) && isFiniteNumber(clientY);
      const isInsideList = Boolean(
        hasFinitePointer &&
          listRect &&
          clientX >= listRect.left &&
          clientX <= listRect.right &&
          clientY >= listRect.top &&
          clientY <= listRect.bottom
      );

      if (isInsideList && sourceIndex !== null && target && listLength > 1) {
        const insertionIndex = getInsertionIndexFromTarget({
          target,
          listLength,
        });

        if (insertionIndex !== null) {
          const destinationIndex = getDestinationIndexFromInsertion({
            sourceIndex,
            insertionIndex,
            listLength,
          });

          commitReorder({
            fromIndex: sourceIndex,
            toIndex: destinationIndex,
          });
        }
      }

      setLiveMessage('Reorder ended');
      resetPointerDrag();
    };

    const onPointerMove = (event: PointerEvent): void => {
      onMove({
        clientX: event.clientX,
        clientY: event.clientY,
      });
    };

    const onMouseMove = (event: MouseEvent): void => {
      onMove({
        clientX: event.clientX,
        clientY: event.clientY,
      });
    };

    const onTouchMove = (event: TouchEvent): void => {
      const pendingPress = pendingPressRef.current;

      if (isDragging || pendingPress?.input === 'touch') {
        event.preventDefault();
      }

      const firstTouch = event.touches[0];
      if (!firstTouch) return;

      onMove({
        clientX: firstTouch.clientX,
        clientY: firstTouch.clientY,
      });
    };

    const onPointerUp = (event: PointerEvent): void => {
      onEnd({
        clientX: event.clientX,
        clientY: event.clientY,
      });
    };

    const onMouseUp = (event: MouseEvent): void => {
      onEnd({
        clientX: event.clientX,
        clientY: event.clientY,
      });
    };

    const onTouchEnd = (event: TouchEvent): void => {
      const touch = event.changedTouches[0];

      onEnd({
        clientX: touch?.clientX,
        clientY: touch?.clientY,
      });
    };

    const onPointerCancel = (): void => {
      onEnd({});
    };

    const onTouchCancel = (): void => {
      onEnd({});
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerCancel);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('touchcancel', onTouchCancel);

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerCancel);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchCancel);
    };
  }, [
    commitReorder,
    dragPreview,
    dragSourceIndex,
    dropTarget,
    getItemRects,
    getListElement,
    isDragging,
    listLength,
    resetPointerDrag,
    startPointerDrag,
  ]);

  useEffect(() => {
    if (!isDragging) return;

    const onEscape = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;

      event.preventDefault();
      setLiveMessage('Reorder cancelled');
      resetPointerDrag();
    };

    window.addEventListener('keydown', onEscape);

    return () => {
      window.removeEventListener('keydown', onEscape);
    };
  }, [isDragging, resetPointerDrag]);

  useEffect(() => {
    if (keyboardState.mode !== 'dragging') return;

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setKeyboardState(createKeyboardDragState());
        setLiveMessage('Reorder cancelled');
        resetPointerDrag();
        return;
      }

      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault();

        setKeyboardState((previous) => {
          const nextState = moveKeyboardDragItem({
            state: previous,
            direction: event.key === 'ArrowUp' ? 'up' : 'down',
            listLength,
          });

          if (nextState.currentIndex !== null) {
            setLiveMessage(`Move target ${nextState.currentIndex + 1}`);
          }

          return nextState;
        });
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [keyboardState.mode, listLength, resetPointerDrag]);

  return {
    isDragging,
    dragItemKey,
    dragSourceIndex,
    dragPreview,
    keyboardState,
    activeDropTarget: isDragging ? dropTarget : keyboardDropTarget,
    liveMessage,
    isKeyboardDragging: keyboardState.mode === 'dragging',
    handlePressStart,
    consumeSuppressedRowClick,
    isRowActionBlocked,
  };
}
